import React, { useState, useEffect, useRef } from 'react';
import {
  getServiceAgreements,
  deleteServiceAgreement,
  saveServiceAgreement,
  getServiceAgreement,
  getClients,
  getSettings,
  getRecurringScheduleByAgreement
} from '../utils/storageUtils';
import RecurringShiftSetup from '../components/RecurringShiftSetup';
import { generateServiceAgreementPDF } from '../utils/serviceAgreementPDF';
import { STANDARD_CLAUSES } from '../utils/agreement-templates';
import { format } from 'date-fns';
import '../App.css';

export default function ServiceAgreementsList() {
  const [agreements, setAgreements] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingAgreement, setViewingAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteId, setShowDeleteId] = useState(null);
  const [showRecurringSetup, setShowRecurringSetup] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Filter agreements when filters change
  useEffect(() => {
    filterAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreements, filterStatus, filterClient, searchTerm]);

  const loadData = () => {
    try {
      const allAgreements = getServiceAgreements();
      const allClients = getClients();
      setAgreements(allAgreements);
      setClients(allClients);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const filterAgreements = () => {
    let filtered = [...agreements];

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(agreement => agreement.status === filterStatus);
    }

    // Filter by client
    if (filterClient !== 'all') {
      filtered = filtered.filter(agreement =>
        agreement.participant && agreement.participant.id === filterClient
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(agreement =>
        (agreement.participant?.name || '').toLowerCase().includes(term) ||
        (agreement.provider?.name || '').toLowerCase().includes(term) ||
        (agreement.agreementNumber || '').toLowerCase().includes(term)
      );
    }

    setFilteredAgreements(filtered);
  };

  const handleDeleteAgreement = (agreementId) => {
    if (window.confirm('Are you sure you want to delete this service agreement? This cannot be undone.')) {
      deleteServiceAgreement(agreementId);
      loadData(); // Reload data
    }
  };

  const handleEditAgreement = (agreementId) => {
    // Navigate to service agreement form with loaded data
    window.location.href = `/service-agreements?edit=${agreementId}`;
  };

  const handleDownloadAgreement = (agreementId) => {
    const agreement = getServiceAgreement(agreementId);
    if (agreement) {
      try {
        const settings = getSettings();
        const pdf = generateServiceAgreementPDF(agreement, settings);
        const participantName = agreement.participant?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Participant';
        const filename = `Service_Agreement_${agreement.agreementNumber || 'SA-' + agreementId.substring(0, 8)}_${participantName}.pdf`;
        pdf.save(filename);
      } catch (error) {
        alert('Error generating PDF. Please try editing the agreement and using the Generate PDF button.');
      }
    }
  };

  const handleViewAgreement = (agreementId) => {
    const agreement = getServiceAgreement(agreementId);
    if (agreement) {
      setViewingAgreement(agreement);
    }
  };

  const checkShowRecurringSetup = (agreement) => {
    if (agreement.status && agreement.status.toLowerCase() === 'active' && agreement.signedDocument) {
      const existing = getRecurringScheduleByAgreement(agreement.id);
      if (!existing) {
        setShowRecurringSetup(agreement);
      }
    }
  };

  const handleStatusChange = (agreementId, newStatus) => {
    const agreement = getServiceAgreement(agreementId);
    if (agreement) {
      const updatedAgreement = {
        ...agreement,
        status: newStatus,
        lastModified: new Date().toISOString()
      };
      saveServiceAgreement(updatedAgreement);
      loadData();
      checkShowRecurringSetup(updatedAgreement);
    }
  };

  // File upload handling
  const fileInputRef = useRef(null);
  const uploadingForId = useRef(null);

  const handleUploadClick = (agreementId) => {
    uploadingForId.current = agreementId;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingForId.current) return;

    const reader = new FileReader();
    reader.onload = () => {
      const agreement = getServiceAgreement(uploadingForId.current);
      if (agreement) {
        const updatedAgreement = {
          ...agreement,
          signedDocument: {
            fileName: file.name,
            fileData: reader.result,
            fileType: file.type,
            uploadedAt: new Date().toISOString()
          },
          lastModified: new Date().toISOString()
        };
        saveServiceAgreement(updatedAgreement);
        loadData();
        checkShowRecurringSetup(updatedAgreement);
      }
      uploadingForId.current = null;
    };
    reader.readAsDataURL(file);
  };

  const handleFileDownload = (signedDocument) => {
    const link = document.createElement('a');
    link.href = signedDocument.fileData;
    link.download = signedDocument.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <h2>Loading Service Agreements...</h2>
      </div>
    );
  }

  return (
    <div className="service-agreements-list-page">
      {/* Hidden file input for document uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        style={{ display: 'none' }}
      />

      <div className="page-header">
        <div className="page-header-text">
          <h1>Service Agreements</h1>
          <p className="page-subtitle">Manage your NDIS service agreements and track their status</p>
        </div>
        <div className="page-actions">
          <a href="/service-agreements" className="btn btn-primary">
            Create New Agreement
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-control"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="generated">Generated</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Participant</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="form-control"
            >
              <option value="all">All Participants</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search agreements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-number">{agreements.length}</div>
          <div className="stat-label">Total Agreements</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {agreements.filter(a => a.status === 'draft').length}
          </div>
          <div className="stat-label">Drafts</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {agreements.filter(a => a.status === 'generated').length}
          </div>
          <div className="stat-label">Generated</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {agreements.filter(a => a.status === 'active').length}
          </div>
          <div className="stat-label">Active</div>
        </div>
      </div>

      {/* Agreements List */}
      {filteredAgreements.length === 0 ? (
        <div className="empty-state">
          <h3>No Service Agreements Found</h3>
          <p>
            {agreements.length === 0
              ? 'You haven\'t created any service agreements yet.'
              : 'No agreements match your current filters.'
            }
          </p>
          {agreements.length === 0 && (
            <a href="/service-agreements" className="btn btn-primary">
              Create Your First Agreement
            </a>
          )}
        </div>
      ) : (
        <>
        {/* Desktop: Table view */}
        <div className="agreements-table sa-desktop-only">
          <table className="data-table">
            <thead>
              <tr>
                <th>Agreement #</th>
                <th>Participant</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Signed Agreement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgreements.map(agreement => (
                <tr key={agreement.id}>
                  <td className="agreement-number">
                    {agreement.agreementNumber || `SA-${agreement.id?.substring(0, 8)}`}
                  </td>
                  <td>
                    <div className="participant-info">
                      <div className="participant-name">
                        {agreement.participant?.name || 'No participant selected'}
                      </div>
                      <div className="participant-ndis">
                        NDIS: {agreement.participant?.ndisNumber || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td>
                    {agreement.period?.startDate
                      ? format(new Date(agreement.period.startDate), 'dd/MM/yyyy')
                      : 'N/A'
                    }
                  </td>
                  <td>
                    {agreement.period?.ongoingUntilCancelled
                      ? 'Ongoing'
                      : agreement.period?.endDate
                        ? format(new Date(agreement.period.endDate), 'dd/MM/yyyy')
                        : 'Ongoing'
                    }
                  </td>
                  <td>
                    <select
                      value={agreement.status || 'draft'}
                      onChange={(e) => handleStatusChange(agreement.id, e.target.value)}
                      className="status-select"
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        backgroundColor: {
                          draft: '#F3F4F6',
                          generated: '#D1FAE5',
                          pending: '#FEF3C7',
                          active: '#DBEAFE',
                          expired: '#FEE2E2',
                          cancelled: '#F3F4F6'
                        }[agreement.status || 'draft'] || '#F3F4F6',
                        color: {
                          draft: '#6B7280',
                          generated: '#047857',
                          pending: '#92400E',
                          active: '#1D4ED8',
                          expired: '#DC2626',
                          cancelled: '#7C2D12'
                        }[agreement.status || 'draft'] || '#6B7280'
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="generated">Generated</option>
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td>
                    {agreement.signedDocument ? (
                      <div>
                        <a
                          href="#download"
                          onClick={(e) => { e.preventDefault(); handleFileDownload(agreement.signedDocument); }}
                          title="Download signed agreement"
                          style={{ fontSize: '0.85em', wordBreak: 'break-all' }}
                        >
                          {agreement.signedDocument.fileName}
                        </a>
                        <br />
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleUploadClick(agreement.id)}
                          style={{ marginTop: '4px' }}
                        >
                          Modify
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => handleUploadClick(agreement.id)}
                      >
                        Upload Document
                      </button>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons" style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '4px',
                      minWidth: '140px'
                    }}>
                      <button
                        className="btn btn-small btn-info"
                        onClick={() => handleViewAgreement(agreement.id)}
                        title="View Details"
                      >
                        View
                      </button>
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleEditAgreement(agreement.id)}
                        title="Edit Agreement"
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-small"
                        onClick={() => handleDownloadAgreement(agreement.id)}
                        title="Download PDF"
                        style={{ gridColumn: '1 / -1', background: '#2e7d32', color: '#fff' }}
                      >
                        Download
                      </button>
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', position: 'relative', paddingTop: '4px' }}>
                        <button
                          className="btn-dots"
                          onClick={() => setShowDeleteId(showDeleteId === agreement.id ? null : agreement.id)}
                          title="More options"
                        >
                          ⋯
                        </button>
                        {showDeleteId === agreement.id && (
                          <button
                            className="btn btn-small btn-danger"
                            style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '100%', marginTop: '4px', zIndex: 5, whiteSpace: 'nowrap' }}
                            onClick={() => { handleDeleteAgreement(agreement.id); setShowDeleteId(null); }}
                          >
                            Delete Agreement
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: Card view */}
        <div className="sa-mobile-only clients-grid">
          {filteredAgreements.map(agreement => (
            <div key={agreement.id} className="client-card">
              <div className="client-header">
                <h3>{agreement.participant?.name || 'No participant selected'}</h3>
                <select
                  value={agreement.status || 'draft'}
                  onChange={(e) => handleStatusChange(agreement.id, e.target.value)}
                  className="status-select"
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: {
                      draft: '#F3F4F6',
                      generated: '#D1FAE5',
                      pending: '#FEF3C7',
                      active: '#DBEAFE',
                      expired: '#FEE2E2',
                      cancelled: '#F3F4F6'
                    }[agreement.status || 'draft'] || '#F3F4F6',
                    color: {
                      draft: '#6B7280',
                      generated: '#047857',
                      pending: '#92400E',
                      active: '#1D4ED8',
                      expired: '#DC2626',
                      cancelled: '#7C2D12'
                    }[agreement.status || 'draft'] || '#6B7280'
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="generated">Generated</option>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="client-details">
                <p><strong>Agreement:</strong> {agreement.agreementNumber || `SA-${agreement.id?.substring(0, 8)}`}</p>
                <p><strong>NDIS:</strong> {agreement.participant?.ndisNumber || 'N/A'}</p>
                <p><strong>Start Date:</strong> {agreement.period?.startDate
                  ? format(new Date(agreement.period.startDate), 'dd/MM/yyyy')
                  : 'N/A'
                }</p>
                <p><strong>End Date:</strong> {agreement.period?.ongoingUntilCancelled
                  ? 'Ongoing'
                  : agreement.period?.endDate
                    ? format(new Date(agreement.period.endDate), 'dd/MM/yyyy')
                    : 'Ongoing'
                }</p>
                {agreement.signedDocument ? (
                  <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
                    <p><strong>Signed Document:</strong>{' '}
                      <a
                        href="#download"
                        onClick={(e) => { e.preventDefault(); handleFileDownload(agreement.signedDocument); }}
                        title="Download signed agreement"
                      >
                        {agreement.signedDocument.fileName}
                      </a>
                    </p>
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => handleUploadClick(agreement.id)}
                      style={{ marginTop: '4px' }}
                    >
                      Replace Document
                    </button>
                  </div>
                ) : (
                  <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
                    <button
                      className="btn btn-small btn-primary"
                      onClick={() => handleUploadClick(agreement.id)}
                    >
                      Upload Signed Document
                    </button>
                  </div>
                )}
              </div>
              <div className="client-actions" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px'
              }}>
                <button
                  className="btn btn-small btn-info"
                  onClick={() => handleViewAgreement(agreement.id)}
                  title="View Details"
                >
                  View
                </button>
                <button
                  className="btn btn-small btn-secondary"
                  onClick={() => handleEditAgreement(agreement.id)}
                  title="Edit Agreement"
                >
                  Edit
                </button>
                <button
                  className="btn btn-small"
                  onClick={() => handleDownloadAgreement(agreement.id)}
                  title="Download PDF"
                  style={{ gridColumn: '1 / -1', background: '#2e7d32', color: '#fff' }}
                >
                  Download
                </button>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
                  <button
                    className="btn-dots"
                    onClick={() => setShowDeleteId(showDeleteId === agreement.id ? null : agreement.id)}
                    title="More options"
                  >
                    ⋯
                  </button>
                  {showDeleteId === agreement.id && (
                    <button
                      className="btn btn-small btn-danger"
                      style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '100%', marginTop: '4px', zIndex: 5, whiteSpace: 'nowrap' }}
                      onClick={() => { handleDeleteAgreement(agreement.id); setShowDeleteId(null); }}
                    >
                      Delete Agreement
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Recurring Shift Setup Modal */}
      {showRecurringSetup && (
        <RecurringShiftSetup
          agreement={showRecurringSetup}
          onClose={() => setShowRecurringSetup(null)}
          onSave={() => setShowRecurringSetup(null)}
        />
      )}

      {/* View Agreement Modal — Review-style read-only layout */}
      {viewingAgreement && (
        <div className="modal-overlay" onClick={() => setViewingAgreement(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', padding: '0', position: 'relative' }}>
            {/* Close button */}
            <button
              className="btn-close"
              onClick={() => setViewingAgreement(null)}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', zIndex: 10 }}
            >
              ×
            </button>

            <div className="modal-body" style={{ padding: '1.5rem', paddingTop: '3rem', maxHeight: '85vh', overflowY: 'auto' }}>
              {/* Title */}
              <h2 style={{ textAlign: 'center', color: 'var(--color-primary)', marginBottom: '0.25rem', marginTop: '1rem' }}>NDIS Service Agreement</h2>
              <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                {viewingAgreement.agreementNumber || 'N/A'} &bull; {(viewingAgreement.status || 'draft').charAt(0).toUpperCase() + (viewingAgreement.status || 'draft').slice(1)}
              </p>

              <div className="review-sections">
                {/* Participant Information */}
                {viewingAgreement.participant && (
                  <div className="review-section">
                    <div className="review-header">
                      <h3>Participant Information</h3>
                    </div>
                    <div className="review-content">
                      <div className="review-row">
                        <span>Name:</span>
                        <span>{viewingAgreement.participant.name}</span>
                      </div>
                      <div className="review-row">
                        <span>NDIS Number:</span>
                        <span>{viewingAgreement.participant.ndisNumber || 'Not provided'}</span>
                      </div>
                      {viewingAgreement.participant.dateOfBirth && (
                        <div className="review-row">
                          <span>Date of Birth:</span>
                          <span>{format(new Date(viewingAgreement.participant.dateOfBirth), 'dd MMMM yyyy')}</span>
                        </div>
                      )}
                      {viewingAgreement.participant.contact?.phone && (
                        <div className="review-row">
                          <span>Phone:</span>
                          <span>{viewingAgreement.participant.contact.phone}</span>
                        </div>
                      )}
                      {viewingAgreement.participant.contact?.email && (
                        <div className="review-row">
                          <span>Email:</span>
                          <span>{viewingAgreement.participant.contact.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Agreement Template */}
                {viewingAgreement.template && (
                  <div className="review-section">
                    <div className="review-header">
                      <h3>Agreement Template</h3>
                    </div>
                    <div className="review-content">
                      <div className="review-row">
                        <span>Template:</span>
                        <span>{viewingAgreement.template.name}</span>
                      </div>
                      <div className="review-row">
                        <span>Category:</span>
                        <span>{viewingAgreement.template.category}</span>
                      </div>
                      {viewingAgreement.template.description && (
                        <div className="review-row">
                          <span>Description:</span>
                          <span>{viewingAgreement.template.description}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Provider Information */}
                {viewingAgreement.provider && (
                  <div className="review-section">
                    <div className="review-header">
                      <h3>Provider Information</h3>
                    </div>
                    <div className="review-content">
                      <div className="review-row">
                        <span>Name:</span>
                        <span>{viewingAgreement.provider.name || 'Not provided'}</span>
                      </div>
                      <div className="review-row">
                        <span>ABN:</span>
                        <span>{viewingAgreement.provider.abn || 'Not provided'}</span>
                      </div>
                      {viewingAgreement.provider.contactPerson && (
                        <div className="review-row">
                          <span>Contact Person:</span>
                          <span>{viewingAgreement.provider.contactPerson}</span>
                        </div>
                      )}
                      {viewingAgreement.provider.phone && (
                        <div className="review-row">
                          <span>Phone:</span>
                          <span>{viewingAgreement.provider.phone}</span>
                        </div>
                      )}
                      {viewingAgreement.provider.email && (
                        <div className="review-row">
                          <span>Email:</span>
                          <span>{viewingAgreement.provider.email}</span>
                        </div>
                      )}
                      {viewingAgreement.provider.address && (() => {
                        const addr = viewingAgreement.provider.address;
                        const parts = [addr.street, addr.suburb, addr.state, addr.postcode].filter(Boolean);
                        return parts.length > 0 ? (
                          <div className="review-row">
                            <span>Address:</span>
                            <span>{parts.join(', ')}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}

                {/* Support Items */}
                {viewingAgreement.supportItems && viewingAgreement.supportItems.length > 0 && (
                  <div className="review-section">
                    <div className="review-header">
                      <h3>Support Items ({viewingAgreement.supportItems.length})</h3>
                    </div>
                    <div className="review-content">
                      {viewingAgreement.supportItems.map((item, index) => {
                        let frequencyText = '';
                        if (item.frequency === 'single') {
                          frequencyText = 'Single session';
                        } else {
                          frequencyText = `${item.sessionsPerPeriod} session(s) per ${item.frequency}`;
                        }
                        const location = item.location === 'Other' ? item.otherLocation : item.location;

                        return (
                          <div key={index} className="support-item-review">
                            <div className="item-name-code">
                              <span className="item-name">{item.name}</span>
                              <span className="item-code">({item.itemNumber})</span>
                            </div>
                            <div className="item-details">
                              <span>
                                {frequencyText} • {item.hoursPerSession} hour(s) • ${(item.pricing?.hourlyRate || 0).toFixed(2)}/hour
                                {location ? ` • Location: ${location}` : ''}
                              </span>
                            </div>
                            {item.specialRequirements && (
                              <div className="item-requirements">
                                <strong>Special Requirements:</strong> {item.specialRequirements}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Pricing Summary */}
                <div className="review-section">
                  <div className="review-header">
                    <h3>Pricing Summary</h3>
                  </div>
                  <div className="review-content">
                    {viewingAgreement.pricing?.region && (
                      <div className="review-row">
                        <span>Region:</span>
                        <span style={{ textTransform: 'capitalize' }}>{viewingAgreement.pricing.region.replace('-', ' ')}</span>
                      </div>
                    )}
                    {(() => {
                      let totalWeekly = 0;
                      if (viewingAgreement.supportItems) {
                        viewingAgreement.supportItems.forEach(item => {
                          const rate = parseFloat(item.pricing?.hourlyRate) || 0;
                          const hours = parseFloat(item.hoursPerSession) || 0;
                          const sessions = parseFloat(item.sessionsPerPeriod) || 1;
                          const costPerSession = rate * hours;
                          if (item.frequency === 'daily') totalWeekly += costPerSession * sessions * 7;
                          else if (item.frequency === 'weekly') totalWeekly += costPerSession * sessions;
                          else if (item.frequency === 'fortnightly') totalWeekly += (costPerSession * sessions) / 2;
                          else if (item.frequency === 'monthly') totalWeekly += (costPerSession * sessions) / 4.33;
                        });
                      }
                      const totalMonthly = totalWeekly * 4.33;
                      const totalAnnual = totalWeekly * 52;
                      return (
                        <>
                          <div className="review-row">
                            <span>Estimated Weekly:</span>
                            <span className="cost-highlight">${totalWeekly.toFixed(2)}</span>
                          </div>
                          <div className="review-row">
                            <span>Estimated Monthly:</span>
                            <span className="cost-highlight">${totalMonthly.toFixed(2)}</span>
                          </div>
                          <div className="review-row">
                            <span>Estimated Annual:</span>
                            <span className="cost-highlight">${totalAnnual.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Agreement Period */}
                <div className="review-section">
                  <div className="review-header">
                    <h3>Agreement Period</h3>
                  </div>
                  <div className="review-content">
                    {viewingAgreement.period?.startDate && (
                      <div className="review-row">
                        <span>Commencement Date:</span>
                        <span>{format(new Date(viewingAgreement.period.startDate), 'dd MMMM yyyy')}</span>
                      </div>
                    )}
                    {viewingAgreement.period?.ongoingUntilCancelled ? (
                      <div className="review-row">
                        <span>Duration:</span>
                        <span>No fixed end date — ongoing agreement</span>
                      </div>
                    ) : viewingAgreement.period?.endDate ? (
                      <div className="review-row">
                        <span>End Date:</span>
                        <span>{format(new Date(viewingAgreement.period.endDate), 'dd MMMM yyyy')}</span>
                      </div>
                    ) : null}
                    <div className="review-row">
                      <span>Payment Terms:</span>
                      <span>{viewingAgreement.terms?.paymentTerms || 'Monthly in arrears'}</span>
                    </div>
                    <div className="review-row">
                      <span>Cancellation Notice:</span>
                      <span>{viewingAgreement.terms?.cancellationNotice || '14 days'}</span>
                    </div>
                  </div>
                </div>

                {/* Terms & Conditions — expanded inline */}
                {((viewingAgreement.terms?.selectedClauses && viewingAgreement.terms.selectedClauses.length > 0) ||
                  (viewingAgreement.terms?.customClauses && viewingAgreement.terms.customClauses.length > 0)) && (
                  <div className="review-section">
                    <div className="review-header">
                      <h3>Terms & Conditions</h3>
                    </div>
                    <div className="review-content">
                      {viewingAgreement.terms.selectedClauses?.map(clauseId => {
                        const clause = STANDARD_CLAUSES[clauseId];
                        if (!clause) return null;
                        return (
                          <div key={clauseId} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                            <p style={{ fontWeight: 'bold', color: 'var(--color-text)', marginBottom: '0.25rem' }}>{clause.title}</p>
                            <p style={{ whiteSpace: 'pre-line', color: 'var(--color-text-secondary)', fontSize: '0.9em', lineHeight: '1.5' }}>{clause.content}</p>
                          </div>
                        );
                      })}
                      {viewingAgreement.terms.customClauses?.map((customClause, index) => {
                        const title = typeof customClause === 'string' ? `Custom Clause ${index + 1}` : customClause.title;
                        const content = typeof customClause === 'string' ? customClause : customClause.content;
                        return (
                          <div key={`custom-${index}`} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                            <p style={{ fontWeight: 'bold', color: 'var(--color-text)', marginBottom: '0.25rem' }}>{title}</p>
                            <p style={{ whiteSpace: 'pre-line', color: 'var(--color-text-secondary)', fontSize: '0.9em', lineHeight: '1.5' }}>{content}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Signatures */}
                <div className="review-section">
                  <div className="review-header">
                    <h3>Agreement Signatures</h3>
                  </div>
                  <div className="review-content">
                    <div className="sa-view-signature-grid">
                      <div className="sa-view-signature-block">
                        <p><strong>PARTICIPANT:</strong></p>
                        <div className="sa-view-signature-line"></div>
                        <p>Print Name: {viewingAgreement.participant?.name || '________________'}</p>
                        <p>Date: ________________</p>
                      </div>
                      <div className="sa-view-signature-block">
                        <p><strong>PROVIDER:</strong></p>
                        <div className="sa-view-signature-line"></div>
                        <p>Print Name: {viewingAgreement.provider?.contactPerson || '________________'}</p>
                        <p>Date: ________________</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Download PDF button */}
              <div style={{ textAlign: 'center', padding: '1rem 0 0.5rem 0' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => handleDownloadAgreement(viewingAgreement.id)}
                >
                  📄 Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}